import { useCallback, useEffect, useState } from "react";
import { BooleanInput, Form, ResourceContextProvider, SimpleForm, Title, useNotify, useRedirect } from "react-admin";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import BackButtonToolbar from "features/BackButtonToolbar";
import InactiveDeleteOptionEditToolbar from "features/EditToolbar";
import dataProvider from "server/dataProvider";
import { AdministrationServicePath, STRUCTURAL_UNITS, STUDY_DEPARTMENT_INFO } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";
import EditStudyDepartmentInfoForm from "./EditForm";

// A record that was never saved comes back with workingTimeFrom = "0001-01-01T00:00:00" (DateTime default)
// instead of null - treat it as empty so the time picker does not show 00:00.
const normalizeTime = value => (!value || value.startsWith("0001-01-01") ? null : value);

const EditStudyDepartmentInfo = () => {
  const { id } = useParams();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const redirect = useRedirect();
  const [isForeigner, setIsForeigner] = useState(false);
  const [record, setRecord] = useState(null);

  const fetchRecord = useCallback(async () => {
    setRecord(null);
    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getStudyDepartmentInfo(id, isForeigner),
        null,
        "GET",
      );
      // the response is an array even when filtered by isForeigner
      const info = data?.find(item => item.isForeigner === isForeigner) ?? {};
      setRecord({
        ...info,
        isForeigner,
        workingTimeFrom: normalizeTime(info.workingTimeFrom),
        workingTimeTo: normalizeTime(info.workingTimeTo),
      });
    } catch (err) {
      console.error(err);
      notify("Грешка при зареждане на данни. Опитайте да презаредите страницата.", { type: "error" });
    }
  }, [id, isForeigner, notify]);

  useEffect(() => {
    if (id) fetchRecord();
  }, [id, fetchRecord]);

  const handleSubmit = async values => {
    try {
      await dataProvider.customRequest(
        administrationRequests.updateStudyDepartmentInfo(id),
        {
          // always sent explicitly - a missing value is treated as false and overwrites the bulgarians record
          isForeigner: record.isForeigner,
          addressBg: values.addressBg,
          addressEn: values.addressEn,
          workingTimeFrom: values.workingTimeFrom,
          workingTimeTo: values.workingTimeTo,
          enrollmentDocumentsBg: values.enrollmentDocumentsBg,
          enrollmentDocumentsEn: values.enrollmentDocumentsEn,
        },
        "PUT",
      );
      notify("Успешно редактиране на информация за учебен отдел", { type: "success" });
      redirect("/" + AdministrationServicePath + STRUCTURAL_UNITS);
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <>
      <Title title={`Редакция на информация за учебен отдел за ${isForeigner ? "чужденци" : "българи"}`} />
      <BackButtonToolbar />
      <ResourceContextProvider value={AdministrationServicePath + STUDY_DEPARTMENT_INFO}>
        {/* Separate form - switching loads the other record, it is not part of the saved values */}
        <Form defaultValues={{ isForeigner: false }}>
          <Box sx={{ paddingX: 2 }}>
            <BooleanInput
              label="За чужденци"
              source="isForeigner"
              onChange={event => setIsForeigner(event.target.checked)}
            />
          </Box>
        </Form>
        {record?.isForeigner === isForeigner && (
          <SimpleForm
            key={String(record.isForeigner)}
            defaultValues={record}
            onSubmit={handleSubmit}
            toolbar={<InactiveDeleteOptionEditToolbar />}
          >
            <EditStudyDepartmentInfoForm />
          </SimpleForm>
        )}
      </ResourceContextProvider>
    </>
  );
};

export default EditStudyDepartmentInfo;
